<app-pan-card [isPageContainer]="true" [header]="'Inspect a Review'" pageContainerSize="xl">
  <div class="flex-auto flex items-center gap-2">
    <app-pan-form-control class="flex-1">
      <app-pan-floating-label>Assignment ID</app-pan-floating-label>
      <input
        panInput
        placeholder="26c07a57-bc11..."
        [(ngModel)]="assignmentIdToInspect"
        (keyup.enter)="isAssignmentIdValid() && loadAssignment(assignmentIdToInspect())"
        [attr.pattern]="uuidRegex"
        [attr.aria-invalid]="assignmentIdToInspect() && !isAssignmentIdValid() ? true : null"
      />
    </app-pan-form-control>

    <app-pan-button
      color="primary"
      class="self-end"
      (click)="loadAssignment(assignmentIdToInspect())"
      [state]="isLoading() ? 'loading' : 'default'"
      [disabled]="!isAssignmentIdValid() || isLoading()"
    >
      Inspect
    </app-pan-button>
  </div>

  @if (assignmentIdToInspect() && !isAssignmentIdValid()) {
    <div class="mt-1">
      <small class="text-red-600" aria-live="polite">Enter a valid assignment ID.</small>
    </div>
  }

  @if (loadErrorMessage()) {
    <app-pan-inline-alert type="danger" class="m-2">
      {{ loadErrorMessage() }}
    </app-pan-inline-alert>
  }

  @let assignment = assignmentBeingInspected();

  @if (assignment && !isLoading()) {
    <div class="flex-auto py-2">
      <div [hidden]="assignment.subject_type.toLowerCase() === ReviewSubjectType.Validation">
        <app-review-form-header>
          <div title>
            <span class="text-xl font-bold">Inspect</span>

            <app-pan-chip class="ml-2" size="sm" color="info">{{ assignmentType() }}</app-pan-chip>
          </div>

          <div actions class="flex items-center gap-2">
            <app-pan-chip size="lg">Status: {{ assignmentStatus() | titlecase }}</app-pan-chip>

            <app-pan-button
              color="primary"
              icon="edit"
              (click)="reviewModal.show()"
              [state]="isSubmitting() ? 'loading' : 'default'"
              [disabled]="isSubmitting()"
              data-testid="editReview"
            >
              Edit Review
            </app-pan-button>

            @if (assignmentStatus() === ReviewStatus.Error) {
              <span [matTooltip]="!isRecoverEnabled() ? recoverDisabledReason() : ''" matTooltipPosition="below">
                <app-pan-button
                  fill="outline"
                  color="info"
                  (click)="recoverReview()"
                  [state]="isRecovering() ? 'loading' : 'default'"
                  [disabled]="!isRecoverEnabled() || isRecovering()"
                >
                  Recover
                </app-pan-button>
              </span>
            }
            <app-pan-button fill="outline" color="info" (click)="forceValidateReview()" [disabled]="!isForceReviewEnabled()">
              Force Validate
            </app-pan-button>
            <app-video-password-actions
              [reviewId]="assignmentIdToInspect()"
              [passwordAction]="passwordAction()"
            ></app-video-password-actions>
          </div>
        </app-review-form-header>
      </div>

      <app-pan-modal [shouldBodyScroll]="true" #reviewModal [topLevel]="false" size="lg">
        <div header>Submit review</div>

        <div body>
          @if (incidentFormRef()) {
            <ng-container [ngTemplateOutlet]="incidentFormRef()"></ng-container>
          }

          <swal
            #submittedSwal
            title="Incident submitted 🎉"
            text="Thank you for the review!"
            icon="success"
            [showCancelButton]="true"
            [showConfirmButton]="false"
            [focusCancel]="true"
            cancelButtonText="Go back"
          ></swal>
          <app-review-submission-failed-modal #failedModal [errorMessage]="submitErrorMessage()"></app-review-submission-failed-modal>
        </div>

        <div footer>
          <button
            panButton
            panModalClose
            color="primary"
            (click)="submitReview()"
            [disabled]="isSubmitting() || !reviewFormService.isFormSubmittable()"
            [state]="isSubmitting() ? 'loading' : 'default'"
            data-testid="submitReview"
          >
            Submit
          </button>
          <app-pan-button panModalClose>Cancel</app-pan-button>
        </div>
      </app-pan-modal>

      @if ([ReviewStatus.Completed, ReviewStatus.Validated].indexOf(assignmentStatus()) !== -1) {
        <app-pan-inline-alert type="success" class="my-2">
          This assignment was completed by <b>{{ assignment.email }}</b> on {{ assignment.completed_at | date: "medium" }}.
        </app-pan-inline-alert>
      }

      @switch (assignmentType()) {
        @case (ReviewSubjectType.Cancellation) {
          <app-transaction-incident-form
            [incidentDateEditable]="true"
            [locationTimezone]="assignment.location_timezone"
            (contextReady)="onContextReady($event)"
            (formRefReady)="incidentFormRef.set($event)"
          />
        }
        @case (ReviewSubjectType.Underringing) {
          <app-transaction-incident-form
            [incidentDateEditable]="true"
            [locationTimezone]="assignment.location_timezone"
            (contextReady)="onContextReady($event)"
            (formRefReady)="incidentFormRef.set($event)"
          />
        }
        @case (ReviewSubjectType.Sideloaded) {
          <app-sideloaded-incident-form
            [locationTimezone]="assignment.location_timezone"
            (contextReady)="onContextReady($event)"
            (formRefReady)="incidentFormRef.set($event)"
          />
        }
        @case (ReviewSubjectType.Deepdive) {
          <app-sideloaded-incident-form
            [locationTimezone]="assignment.location_timezone"
            (contextReady)="onContextReady($event)"
            (formRefReady)="incidentFormRef.set($event)"
          />
        }
        @case (ReviewSubjectType.Grocery) {
          <app-grocery-incident-form
            [locationTimezone]="assignment.location_timezone"
            (contextReady)="onContextReady($event)"
            (formRefReady)="incidentFormRef.set($event)"
          />
        }
        @case (ReviewSubjectType.Walkout) {
          <app-walkout-incident-form
            [locationTimezone]="assignment.location_timezone"
            [incidentDateEditable]="true"
            (contextReady)="onContextReady($event)"
            (formRefReady)="incidentFormRef.set($event)"
          />
        }
        <!--  Leaving a comment for reference: this will actually never load here cause when we load a validation,
      we instead load the original review, so this case doesn't make sense with current implementation.
      The reason I don't remove it is so that we catch weird behavior (we would see the wrong form) and also to help
      inform what types of forms could be loaded. Probably tech debt to take care of outside a hotfix.
-->
        @case (ReviewSubjectType.Validation) {
          <app-validation-assignment [initialLocationTimezone]="assignment.location_timezone" [validationId]="assignmentIdToInspect()!" />
        }
      }
    </div>
  }
</app-pan-card>
